import * as React from 'react';
import { cn } from '@/lib/utils';
import { usageTone } from '@/lib/usage-utils';

interface UsageBarProps {
  value: number;
  label?: React.ReactNode;
  valueLabel?: React.ReactNode;
  // "threshold" applies the usage semaphore; "primary" is a neutral brand color
  // for relative shares where a high value is not a limit warning.
  tone?: 'threshold' | 'primary';
  size?: 'sm' | 'md';
  className?: string;
}

export function UsageBar({
  value,
  label,
  valueLabel,
  tone = 'threshold',
  size = 'md',
  className,
}: UsageBarProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const fillClass = tone === 'primary' ? 'bg-primary' : usageTone(clamped).bg;
  const width = clamped > 0 ? Math.max(clamped, 2) : 0;

  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || valueLabel) && (
        <div className="flex items-center justify-between gap-2 text-sm">
          {label && <span className="min-w-0 truncate font-medium">{label}</span>}
          {valueLabel && (
            <span className="shrink-0 font-mono text-sm font-bold">{valueLabel}</span>
          )}
        </div>
      )}
      <div
        className={cn('w-full overflow-hidden rounded-full bg-muted', size === 'sm' ? 'h-2' : 'h-2.5')}
      >
        <div
          className={cn('h-full rounded-full transition-all duration-700 ease-out', fillClass)}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}
